import { useCallback, useEffect, useRef, useState } from 'react';

import {
    EarningsRecord,
    GetEarningsInput,
    GetTerritoryUsageInput,
    TerritoryUsageRecord
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';

import { contractConfigurationClient } from 'src/api/client';
import { getRecentMonths } from 'src/components/Analytics/utils/dateUtils';

import { MonthDataAccumulator, ProgressiveDataState } from './types';

export interface RoyaltiesGridData {
    month: string;
    territory: string;
    vendor: string;
    earning: number;
    currency: string;
    earningInUSD: number;
}

export interface MssGridData {
    month: string;
    territory: string;
    vendor: string;
    vendorPlays: number;
    totalPlays: number;
    mss: number;
    // Music-only fields (optional - only populated for AMU serviceId=1 AND UMG & affiliate vendors)
    musicOnlyVendorPlays?: number;
    musicOnlyTotalPlays?: number;
    musicOnlyMss?: number;
    // MSS Type field (only for AMU serviceId=1)
    isArtistCentric?: boolean;
    eligibleForEarningsSubmission: boolean;
}

export interface MssPivotGridData {
    month: string;
    territory: string;
    vendor: string;
    // ACTM data (isArtistCentric=true)
    actmVendorPlays?: number;
    actmTotalPlays?: number;
    actmMss?: number;
    actmMusicOnlyVendorPlays?: number;
    actmMusicOnlyTotalPlays?: number;
    actmMusicOnlyMss?: number;
    // Standard data (isArtistCentric=false)
    standardVendorPlays?: number;
    standardTotalPlays?: number;
    standardMss?: number;
    standardMusicOnlyVendorPlays?: number;
    standardMusicOnlyTotalPlays?: number;
    standardMusicOnlyMss?: number;
    // Indicates which mode is eligible for earnings submission
    eligibleMode?: 'actm' | 'standard' | 'none';
}

export type ServiceDataType = 'royalties' | 'mss';

interface ServiceConfig {
    serviceId: number;
    dataType: ServiceDataType;
    months?: string[]; // Optional months array - defaults to recent 6 months
}

// Constants
const BATCH_SIZE = 10000;

/**
 * Generic progressive loading hook for service data (Royalties or MSS)
 */
const useProgressiveServiceDataGeneric = <T extends RoyaltiesGridData | MssGridData>(
    config: ServiceConfig
): ProgressiveDataState<T> => {
    const { serviceId, dataType, months } = config;

    // Use provided months or default to recent 6 months
    const monthsToUse = months || getRecentMonths(6);

    // Simplified state management
    const [data, setData] = useState<T[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [hasStartedLoading, setHasStartedLoading] = useState(false);
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
    const [completionPercentage, setCompletionPercentage] = useState(0);
    const monthAccumulators = useRef(new Map<string, MonthDataAccumulator<EarningsRecord | TerritoryUsageRecord>>());

    const transformRoyaltiesResponse = (records: EarningsRecord[]): RoyaltiesGridData[] => {
        return records
            .filter((record) => {
                // Filter out shadow entities
                const entityName = record.entity || '';
                if (entityName.toLowerCase().includes('shadow')) {
                    return false;
                }

                // Only include records eligible for earnings submission
                return record.eligibleForEarningsSubmission == true;
            })
            .map((record) => {
                return {
                    month: record.month ?? '',
                    territory: record.territoryCode ?? '',
                    vendor: record.entity ?? '',
                    earning: record.amount ?? 0,
                    currency: record.currency ?? '',
                    earningInUSD: record.amountInUsd ?? 0
                };
            });
    };

    // MSS-specific utilities
    const createMssDuplicateKey = (record: TerritoryUsageRecord): string => {
        return `${record.month || ''}-${record.territoryCode || ''}-${record.entity || ''}`;
    };

    const isUmgAffiliate = (vendor: string): boolean => {
        if (!vendor) return false;
        const vendorLower = vendor.toLowerCase();
        return vendorLower.includes('universal') && vendorLower.includes('affiliate');
    };

    const calculateSmartMss = (
        standardVendorShare: number,
        playsForVendorPlanCombined: number,
        playsForPlanCombined: number
    ): number => {
        if (standardVendorShare === 0.0 && playsForVendorPlanCombined > 0 && playsForPlanCombined > 0) {
            const calculated = playsForVendorPlanCombined / playsForPlanCombined;
            if (calculated < 0.000001) {
                return 0;
            } else {
                return calculated;
            }
        } else {
            return standardVendorShare;
        }
    };

    const transformMssResponse = (records: TerritoryUsageRecord[], serviceId: number): MssGridData[] => {
        const recordGroups = new Map<string, TerritoryUsageRecord[]>();

        // Group records by month + territory + entity combination
        records.forEach((record) => {
            const entityName = record.entity || '';
            if (entityName.toLowerCase().includes('shadow')) {
                return;
            }

            const duplicateKey = createMssDuplicateKey(record);
            if (!recordGroups.has(duplicateKey)) {
                recordGroups.set(duplicateKey, []);
            }
            recordGroups.get(duplicateKey)?.push(record);
        });

        // For each group, keep at most 2 records: one with eligibleForEarningsSubmission=true and one with eligibleForEarningsSubmission=false
        const selectedRecords: TerritoryUsageRecord[] = [];
        recordGroups.forEach((groupRecords) => {
            // Find record with eligibleForEarningsSubmission = true
            const eligibleRecord = groupRecords.find((record) => record.eligibleForEarningsSubmission === true);

            // Find record with eligibleForEarningsSubmission = false
            const nonEligibleRecord = groupRecords.find((record) => record.eligibleForEarningsSubmission === false);

            // Add both records if they exist (maintaining both true and false records)
            if (eligibleRecord) {
                selectedRecords.push(eligibleRecord);
            }
            if (nonEligibleRecord) {
                selectedRecords.push(nonEligibleRecord);
            }
        });

        return selectedRecords.map((record) => {
            const smartMss = calculateSmartMss(
                record.standardVendorShare || 0,
                record.playsForVendorPlanCombined || 0,
                record.playsForPlanCombined || 0
            );

            const isAmuService = serviceId === 1;
            const isUmgVendor = isUmgAffiliate(record.entity || '');
            const shouldPopulateMusicOnlyFields = isAmuService && isUmgVendor;

            return {
                month: record.month || '',
                territory: record.territoryCode || '',
                vendor: record.entity || '',
                vendorPlays: record.playsForVendorPlanCombined || 0,
                totalPlays: record.playsForPlanCombined || 0,
                mss: smartMss,
                // Music-only fields (only for AMU serviceId=1 AND UMG & affiliate)
                musicOnlyVendorPlays: shouldPopulateMusicOnlyFields
                    ? record.playsForVendorPlanMusicOnly || 0
                    : undefined,
                musicOnlyTotalPlays: shouldPopulateMusicOnlyFields ? record.playsForPlanMusicOnly || 0 : undefined,
                musicOnlyMss: shouldPopulateMusicOnlyFields ? record.antiDilutionVendorShare || 0 : undefined,
                // MSS Type field (only for AMU serviceId=1)
                isArtistCentric: isAmuService ? record.isArtistCentric : undefined,
                eligibleForEarningsSubmission: record.eligibleForEarningsSubmission ?? false
            };
        });
    };

    // Generic data transformation
    const transformApiResponse = useCallback(
        (records: (EarningsRecord | TerritoryUsageRecord)[]): T[] => {
            if (dataType === 'royalties') {
                return transformRoyaltiesResponse(records as EarningsRecord[]) as T[];
            } else {
                return transformMssResponse(records as TerritoryUsageRecord[], serviceId) as T[];
            }
        },
        [dataType, serviceId]
    );

    const loadBatch = useCallback(
        async (month: string, batchNumber: number): Promise<(EarningsRecord | TerritoryUsageRecord)[]> => {
            if (dataType === 'royalties') {
                const response = await contractConfigurationClient
                    .getEarnings(
                        GetEarningsInput.clone({
                            serviceId,
                            month,
                            batchNumber,
                            batchSize: BATCH_SIZE
                        })
                    )
                    .toPromise();

                return response?.earningsRecords || [];
            } else {
                const response = await contractConfigurationClient
                    .getTerritoryUsage(
                        GetTerritoryUsageInput.clone({
                            serviceId,
                            month,
                            batchNumber,
                            batchSize: BATCH_SIZE
                        })
                    )
                    .toPromise();

                return response?.usageRecords || [];
            }
        },
        [serviceId, dataType]
    );

    // Update combined data from all accumulators
    const updateCombinedData = useCallback(() => {
        const allRecords: (EarningsRecord | TerritoryUsageRecord)[] = [];

        monthAccumulators.current.forEach((accumulator) => {
            allRecords.push(...accumulator.data);
        });

        const transformedData = transformApiResponse(allRecords);
        setData(transformedData);

        // Return the transformed data so we can check if it has content
        return transformedData;
    }, [transformApiResponse]);

    // Simplified month loading with progress tracking
    const loadCompleteMonth = useCallback(
        async (month: string): Promise<void> => {
            try {
                let batchNumber = 1;
                const allRecords: (EarningsRecord | TerritoryUsageRecord)[] = [];

                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const batchRecords = await loadBatch(month, batchNumber);

                    if (batchRecords.length === 0) {
                        // Empty response - month is complete
                        break;
                    } else {
                        // Add records and continue
                        allRecords.push(...batchRecords);
                        batchNumber++;
                    }
                }

                // Store accumulated data
                monthAccumulators.current.set(month, {
                    month,
                    data: allRecords,
                    isComplete: true,
                    batchesReceived: batchNumber - 1,
                    lastBatchNumber: batchNumber - 1
                });

                // Update combined data and get the result
                const currentTransformedData = updateCombinedData();

                // Update progress based on completed months
                const completedMonths = monthAccumulators.current.size;
                const totalMonths = monthsToUse.length;
                const progressPercentage = Math.round((completedMonths / totalMonths) * 100);
                setCompletionPercentage(progressPercentage);

                // Set initial load complete only when we have actual data to show
                if (!isInitialLoadComplete && currentTransformedData.length > 0) {
                    setIsInitialLoadComplete(true);
                }
            } catch (error) {
                const errorMessage =
                    error instanceof UnknownCoralError
                        ? error.receivedException.message
                        : `Failed to load ${dataType} data for ${month}`;

                setError(errorMessage);
            }
        },
        [loadBatch, dataType, updateCombinedData, isInitialLoadComplete, monthsToUse]
    );

    // Optimized loading strategy: Royalties recent month first, then parallel loading
    const startDataLoading = useCallback(async () => {
        try {
            setError(null);
            setCompletionPercentage(0);

            if (monthsToUse.length === 0) {
                setIsInitialLoadComplete(true);
                return;
            }

            if (dataType === 'royalties') {
                // ROYALTIES STRATEGY: Load most recent month first, then parallel for rest
                // Phase 1: Load most recent month first
                const mostRecentMonth = monthsToUse[monthsToUse.length - 1];
                await loadCompleteMonth(mostRecentMonth);

                // Phase 2: Load remaining months in parallel (if any)
                const remainingMonths = monthsToUse.slice(0, -1);
                if (remainingMonths.length > 0) {
                    const remainingPromises = remainingMonths.map((month) => loadCompleteMonth(month));
                    await Promise.allSettled(remainingPromises);
                }
            } else {
                // MSS STRATEGY: Load all months in parallel (since MSS tab is secondary)
                const monthPromises = monthsToUse.map((month) => loadCompleteMonth(month));
                await Promise.allSettled(monthPromises);
            }

            // If all months are loaded but we still don't have initial load complete,
            // it means all months returned empty data - show the empty state
            if (!isInitialLoadComplete) {
                setIsInitialLoadComplete(true);
            }

            // Final completion percentage should be 100% (handled in loadCompleteMonth)
        } catch (error) {
            const errorMessage =
                error instanceof UnknownCoralError
                    ? error.receivedException.message
                    : `Failed to load ${dataType} data`;
            setError(errorMessage);
        }
    }, [loadCompleteMonth, dataType, isInitialLoadComplete, monthsToUse]);

    // Reset state when months change
    useEffect(() => {
        // Clear previous data when months change
        monthAccumulators.current.clear();
        setData([]);
        setError(null);
        setIsInitialLoadComplete(false);
        setCompletionPercentage(0);
        setHasStartedLoading(false);
    }, [JSON.stringify(monthsToUse)]);

    // Start loading on mount or when months change
    useEffect(() => {
        if (!hasStartedLoading && monthsToUse.length > 0) {
            setHasStartedLoading(true);
            startDataLoading();
        } else if (monthsToUse.length === 0) {
            // Handle empty months array - set as complete with no data
            setIsInitialLoadComplete(false);
            setCompletionPercentage(0);
        }
    }, [startDataLoading, hasStartedLoading, monthsToUse.length]);

    return {
        data,
        error,
        isInitialLoadComplete,
        completionPercentage
    };
};

/**
 * Typed hook for royalties data
 */
export const useProgressiveRoyaltiesData = (
    config: Omit<ServiceConfig, 'dataType'>
): ProgressiveDataState<RoyaltiesGridData> => {
    return useProgressiveServiceDataGeneric<RoyaltiesGridData>({
        ...config,
        dataType: 'royalties'
    });
};

/**
 * Typed hook for MSS data
 */
export const useProgressiveMssData = (config: Omit<ServiceConfig, 'dataType'>): ProgressiveDataState<MssGridData> => {
    return useProgressiveServiceDataGeneric<MssGridData>({
        ...config,
        dataType: 'mss'
    });
};

/**
 * Original export for backward compatibility
 */
export const useProgressiveServiceData = useProgressiveServiceDataGeneric;
